// Client-only Verlet chains (§12.4 noodle arms, balloon string; §11.3 plunger rope). Fixed-size
// typed arrays, fixed sub-steps, no allocation after construction.
import type { PartSkin } from '../materials/partSkin';
import { len3 } from './anim';

const MAX_RINGS = 32;
const centers = new Float32Array(MAX_RINGS * 3);

export class VerletChain {
  readonly n: number;
  readonly p: Float32Array;
  readonly prev: Float32Array;
  /** Per-point pose targets and pull strength (1/s²); a 0 pull leaves the point free. */
  readonly target: Float32Array;
  readonly pull: Float32Array;
  segment: number;
  damping: number;
  gravity: number;
  pinFirst = true;
  pinLast = false;

  constructor(n: number, segment: number, damping = 0.96, gravity = -9.8) {
    this.n = n;
    this.segment = segment;
    this.damping = damping;
    this.gravity = gravity;
    this.p = new Float32Array(n * 3);
    this.prev = new Float32Array(n * 3);
    this.target = new Float32Array(n * 3);
    this.pull = new Float32Array(n);
  }

  /** Places every point on the line from (ax, ay, az) along (dx, dy, dz) and stops all motion. */
  reset(ax: number, ay: number, az: number, dx: number, dy: number, dz: number): void {
    const len = len3(dx, dy, dz) || 1;
    for (let i = 0; i < this.n; i++) {
      const k = (i * this.segment) / len;
      this.p[i * 3] = this.prev[i * 3] = ax + dx * k;
      this.p[i * 3 + 1] = this.prev[i * 3 + 1] = ay + dy * k;
      this.p[i * 3 + 2] = this.prev[i * 3 + 2] = az + dz * k;
    }
  }

  setPoint(i: number, x: number, y: number, z: number): void {
    this.p[i * 3] = x;
    this.p[i * 3 + 1] = y;
    this.p[i * 3 + 2] = z;
  }

  setTarget(i: number, x: number, y: number, z: number, pull: number): void {
    this.target[i * 3] = x;
    this.target[i * 3 + 1] = y;
    this.target[i * 3 + 2] = z;
    this.pull[i] = pull;
  }

  /**
   * Advances by `dt` in sub-steps of ≤ 1/120 s. The first (and, if pinned, last) point must have
   * been placed with `setPoint` beforehand. `ax, ay, az`: frame acceleration felt as a fictitious
   * force (the rig simulates in its own moving frame).
   */
  step(dt: number, ax = 0, ay = 0, az = 0): void {
    let left = Math.min(dt, 0.1);
    const p = this.p;
    const q = this.prev;
    while (left > 1e-6) {
      const h = Math.min(left, 1 / 120);
      const h2 = h * h;
      const damp = Math.pow(this.damping, h * 60);
      for (let i = 0; i < this.n; i++) {
        if ((i === 0 && this.pinFirst) || (i === this.n - 1 && this.pinLast)) continue;
        const o = i * 3;
        const k = this.pull[i];
        const fx = -ax + (k > 0 ? (this.target[o] - p[o]) * k : 0);
        const fy = this.gravity - ay + (k > 0 ? (this.target[o + 1] - p[o + 1]) * k : 0);
        const fz = -az + (k > 0 ? (this.target[o + 2] - p[o + 2]) * k : 0);
        const x = p[o];
        const y = p[o + 1];
        const z = p[o + 2];
        p[o] = x + (x - q[o]) * damp + fx * h2;
        p[o + 1] = y + (y - q[o + 1]) * damp + fy * h2;
        p[o + 2] = z + (z - q[o + 2]) * damp + fz * h2;
        q[o] = x;
        q[o + 1] = y;
        q[o + 2] = z;
      }
      for (let it = 0; it < 4; it++) this.constrain();
      left -= h;
    }
  }

  private constrain(): void {
    const p = this.p;
    const last = this.n - 1;
    for (let i = 0; i < last; i++) {
      const a = i * 3;
      const b = a + 3;
      const dx = p[b] - p[a];
      const dy = p[b + 1] - p[a + 1];
      const dz = p[b + 2] - p[a + 2];
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-6;
      const diff = (d - this.segment) / d;
      const fixedA = i === 0 && this.pinFirst;
      const fixedB = i + 1 === last && this.pinLast;
      if (fixedA && fixedB) continue;
      const wa = fixedA ? 0 : fixedB ? 1 : 0.5;
      const wb = fixedB ? 0 : fixedA ? 1 : 0.5;
      p[a] += dx * diff * wa;
      p[a + 1] += dy * diff * wa;
      p[a + 2] += dz * diff * wa;
      p[b] -= dx * diff * wb;
      p[b + 1] -= dy * diff * wb;
      p[b + 2] -= dz * diff * wb;
    }
  }

  /**
   * Poses a fixed-topology tube (§12.4: rings × sides, positions from a Catmull-Rom curve through
   * the chain) on ring bones `firstBone … firstBone + rings − 1`, radius tapering r0 → r1. Ring
   * frames are parallel-transported so the tube never twists.
   */
  poseTube(skin: PartSkin, firstBone: number, rings: number, r0: number, r1: number): void {
    this.sampleCenters(rings);
    let nx = 0;
    let ny = 0;
    let nz = 0;
    for (let r = 0; r < rings; r++) {
      const a = Math.max(0, r - 1) * 3;
      const b = Math.min(rings - 1, r + 1) * 3;
      let tx = centers[b] - centers[a];
      let ty = centers[b + 1] - centers[a + 1];
      let tz = centers[b + 2] - centers[a + 2];
      const tl = len3(tx, ty, tz);
      if (tl < 1e-6) {
        tx = 0; ty = 1; tz = 0;
      } else {
        tx /= tl; ty /= tl; tz /= tl;
      }
      if (r === 0) {
        // n0 = normalize(ref × t), ref = up unless t is nearly vertical.
        const up = Math.abs(ty) < 0.9;
        nx = up ? -tz : 0;
        ny = up ? 0 : tz;
        nz = up ? tx : -ty;
      } else {
        const d = nx * tx + ny * ty + nz * tz;
        nx -= tx * d;
        ny -= ty * d;
        nz -= tz * d;
      }
      const nl = len3(nx, ny, nz) || 1;
      nx /= nl; ny /= nl; nz /= nl;
      const k = r / (rings - 1);
      const o = r * 3;
      skin.setRing(firstBone + r, centers[o], centers[o + 1], centers[o + 2], tx, ty, tz, nx, ny, nz, r0 + (r1 - r0) * k);
    }
  }

  /** Centers of `rings` points along the Catmull-Rom curve through the chain. */
  private sampleCenters(rings: number): void {
    const p = this.p;
    const segs = this.n - 1;
    for (let r = 0; r < rings; r++) {
      const t = (r / (rings - 1)) * segs;
      const i = Math.min(segs - 1, Math.floor(t));
      const u = t - i;
      const i0 = Math.max(0, i - 1) * 3;
      const i1 = i * 3;
      const i2 = (i + 1) * 3;
      const i3 = Math.min(segs, i + 2) * 3;
      const u2 = u * u;
      const u3 = u2 * u;
      const c0 = -0.5 * u3 + u2 - 0.5 * u;
      const c1 = 1.5 * u3 - 2.5 * u2 + 1;
      const c2 = -1.5 * u3 + 2 * u2 + 0.5 * u;
      const c3 = 0.5 * u3 - 0.5 * u2;
      const o = r * 3;
      centers[o] = p[i0] * c0 + p[i1] * c1 + p[i2] * c2 + p[i3] * c3;
      centers[o + 1] = p[i0 + 1] * c0 + p[i1 + 1] * c1 + p[i2 + 1] * c2 + p[i3 + 1] * c3;
      centers[o + 2] = p[i0 + 2] * c0 + p[i1 + 2] * c1 + p[i2 + 2] * c2 + p[i3 + 2] * c3;
    }
  }

  /** Last point (the hand / knot end). */
  endX(): number { return this.p[(this.n - 1) * 3]; }
  endY(): number { return this.p[(this.n - 1) * 3 + 1]; }
  endZ(): number { return this.p[(this.n - 1) * 3 + 2]; }
}
